import { useEffect, useState } from 'react';
import { useWallet } from '../context/WalletContext';
import { useContracts } from './useContracts';

const EMPTY = {
  registered: false,
  userId: 0,
  core: null,
  packages: [false, false, false, false, false, false, false],
  packageStatuses: [],
  income: null,
  pools: { eliteEligible: false, proEligible: false, globalEligible: false },
  royalties: { eliteClaimable: '0', proClaimable: '0', globalClaimable: '0' },
  heldIncomes: { ids: [], amounts: [], packageIds: [], createdAts: [] },
  activities: [],
  packagePrices: [],
  refLink: ''
};

export const useUserData = (refreshKey = 0) => {
  const { account } = useWallet();
  const { coreStorage, plans } = useContracts();
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [accountKey, setAccountKey] = useState(null);

  useEffect(() => {
    if (accountKey !== account) {
      setAccountKey(account);
      if (!account) {
        setData(EMPTY);
        setLoaded(false);
      }
    }
  }, [account, accountKey]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!coreStorage?.read) return;
      setLoading(true);
      try {
        // Step 1: prices + userId in parallel (the only sequential dependency)
        const [prices, userIdRaw] = await Promise.all([
          coreStorage.read.getAllPackagePrices().catch(() => []),
          account ? coreStorage.read.addressToId(account).catch(() => 0) : Promise.resolve(0)
        ]);

        const uidNum = userIdRaw && userIdRaw.toNumber ? userIdRaw.toNumber() : Number(userIdRaw || 0);

        if (!account || !uidNum) {
          if (!cancelled) {
            setData({ ...EMPTY, packagePrices: prices });
            setLoaded(true);
          }
          return;
        }

        // Step 2: ALL user-data reads in parallel (16 calls, ~1 round-trip)
        const pkgStatusCalls = [1, 2, 3, 4, 5, 6, 7].map((i) =>
          coreStorage.read.getPackageStatus(uidNum, i).catch(() => null)
        );

        const [
          core, pkgsBoolArr, income, pools, held, activities,
          eliteClaim, proClaim, globalClaim,
          ...pkgs
        ] = await Promise.all([
          coreStorage.read.getUserCore(uidNum),
          coreStorage.read.getUserPackages(uidNum),
          coreStorage.read.getUserIncomeBreakdown(uidNum),
          coreStorage.read.getPoolEligibilities(uidNum),
          coreStorage.read.getActiveHeldIncomes(uidNum),
          coreStorage.read.getUserActivities(uidNum).catch(() => []),
          coreStorage.read.getEliteRoyaltyClaimable(uidNum).catch(() => 0),
          coreStorage.read.getProRoyaltyClaimable(uidNum).catch(() => 0),
          coreStorage.read.getGlobalRoyaltyClaimable(uidNum).catch(() => 0),
          ...pkgStatusCalls
        ]);

        const refLink = `${window.location.origin}/?ref=${uidNum}`;

        if (!cancelled) {
          setData({
            registered: true,
            userId: uidNum,
            core: {
              account: core.account,
              sponsorId: core.sponsorId.toString(),
              position: Number(core.position),
              highestPackage: Number(core.highestPackage),
              isActive: core.isActive,
              directReferrals: core.directReferrals.toString(),
              totalReceived: core.totalReceived
            },
            packages: pkgsBoolArr.map((b) => Boolean(b)),
            packageStatuses: pkgs,
            income: {
              sponsorIncome: income.sponsorIncome,
              levelIncome: income.levelIncome,
              boosterIncome: income.boosterIncome,
              eliteRoyalty: income.eliteRoyalty,
              proRoyalty: income.proRoyalty,
              globalRoyalty: income.globalRoyalty,
              totalIncomeGross: income.totalIncomeGross,
              totalReceivedNet: income.totalReceivedNet
            },
            pools: {
              eliteEligible: pools.eliteEligible,
              proEligible: pools.proEligible,
              globalEligible: pools.globalEligible
            },
            royalties: {
              eliteClaimable: eliteClaim.toString(),
              proClaimable: proClaim.toString(),
              globalClaimable: globalClaim.toString()
            },
            heldIncomes: {
              ids: (held.ids || []).map((x) => x.toString()),
              amounts: (held.amounts || []).map((x) => x.toString()),
              packageIds: (held.packageIds || []).map((x) => Number(x)),
              createdAts: (held.createdAts || []).map((x) => Number(x))
            },
            activities: (activities || []).map((a) => ({
              activityType: Number(a.activityType),
              packageId: Number(a.packageId),
              timestamp: Number(a.timestamp),
              fromId: a.fromId.toString(),
              amount: a.amount
            })),
            packagePrices: prices,
            refLink
          });
          setLoaded(true);
        }
      } catch (e) {
        // swallow — keep prior data
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [account, coreStorage, plans, refreshKey]);

  return { ...data, loading, loaded };
};
